import React, { useState, useMemo } from "react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { searchStations } from "@/lib/stations";
import { ChevronsUpDown } from "lucide-react";

/**
 * Free-text input with NSW station autocomplete suggestions.
 * Users can pick from suggestions OR type any custom string (e.g. "Penrith via Strathfield").
 */
export default function StationAutocomplete({
  value,
  onChange,
  placeholder = "Type a station…",
  inputClassName = "",
  testId,
}) {
  const [open, setOpen] = useState(false);
  // Use last 30 chars after final separator (' via ', '-', '→') as query
  const query = useMemo(() => {
    const v = (value || "").toString();
    const parts = v.split(/\s+via\s+|→|->/i);
    return parts[parts.length - 1].trim();
  }, [value]);

  const suggestions = useMemo(() => searchStations(query, 40), [query]);

  const pick = (station) => {
    // Replace last token after separator
    const v = (value || "").toString();
    const m = v.match(/^(.*?(?:\s+via\s+|→|->))\s*[^→\-]*$/i);
    if (m) {
      onChange(`${m[1]} ${station}`.replace(/\s+/g, " ").trim());
    } else {
      onChange(station);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          className={`flex h-10 w-full px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${inputClassName}`}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setOpen((o) => !o)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            aria-label="Show suggestions"
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 rounded-none bg-[#111] border-[#2A2A2A] w-[var(--radix-popover-trigger-width)] min-w-[280px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Command className="bg-[#111] text-white rounded-none" shouldFilter={false}>
          <CommandList className="max-h-64">
            <CommandEmpty className="py-4 text-xs font-mono text-white/40 uppercase tracking-wider">
              No matching stations
            </CommandEmpty>
            <CommandGroup>
              {suggestions.map((s) => (
                <CommandItem
                  key={s}
                  value={s}
                  onSelect={() => pick(s)}
                  className="font-mono text-[12px] data-[selected=true]:bg-white data-[selected=true]:text-black cursor-pointer"
                >
                  {s}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
